import React, { useMemo } from 'react'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    HStack,
    Icon,
    Text,
} from '@chakra-ui/react'
import { addMinutes, areIntervalsOverlapping, format, startOfDay } from 'date-fns'
import { FaCalendarTimes } from 'react-icons/fa'
import {
    AppointmentStatus,
    RescheduleFieldsFragment,
    useSearchAppointmentsQuery,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import {
    getPatientNameFromAppointment,
    getServiceNameFromAppointment,
} from '~utils/appointment-helpers'

interface Props {
    reschedule: RescheduleFieldsFragment
}

/**
 * Surfaces the appointment(s) already occupying a reschedule's *requested* slot
 * so staff can see exactly what would be double-booked before they approve. This
 * is a proactive, client-side check; the server re-validates availability on
 * approval and remains the source of truth (see assertRescheduleSlotIsAvailable).
 */
const RequestedTimeConflicts: React.FC<Props> = ({ reschedule }) => {
    const { clinic } = useClinic()

    const requestedStart = reschedule.requested_start_time
    const durationMinutes = reschedule.appointment.minutes
    // In-place reschedules keep the existing clinician; cancel-and-rebook flows
    // may request a new one. Either way this is the resource that would hold the
    // requested slot, so it's what we check for collisions.
    const clinicianToCheck =
        reschedule.requested_clinician || reschedule.appointment.clinician

    const { data } = useSearchAppointmentsQuery({
        skip: !requestedStart || !clinicianToCheck?.id || !clinic?.id,
        fetchPolicy: 'cache-and-network',
        variables: {
            searchInput: {
                clinicId: clinic?.id || '',
                clinicianId: clinicianToCheck?.id || '',
                start_date: startOfDay(requestedStart || 0).getTime(),
                end_date: addMinutes(
                    startOfDay(requestedStart || 0),
                    24 * 60,
                ).getTime(),
                limit: 100,
            },
        },
    })

    const conflicts = useMemo(() => {
        if (!requestedStart) return []
        const requestedInterval = {
            start: requestedStart,
            end: addMinutes(requestedStart, durationMinutes),
        }
        return (data?.searchAppointments || []).filter(appt => {
            // The appointment being rescheduled shouldn't collide with itself.
            if (appt.id === reschedule.appointment.id) return false
            if (
                appt.status === AppointmentStatus.Cancelled ||
                appt.status === AppointmentStatus.Denied
            ) {
                return false
            }
            return areIntervalsOverlapping(
                {
                    start: appt.start_time,
                    end: addMinutes(appt.start_time, appt.minutes),
                },
                requestedInterval,
            )
        })
    }, [data, requestedStart, durationMinutes, reschedule.appointment.id])

    if (!requestedStart || conflicts.length === 0) return null

    const clinicianName =
        clinicianToCheck?.name?.trim() || 'this clinician'

    return (
        <Box px={{ base: 4, md: 0 }} pt={4}>
            <Alert
                status="error"
                variant="left-accent"
                borderRadius="md"
                flexDir="column"
                alignItems="flex-start"
                gap={2}
            >
                <HStack>
                    <AlertIcon />
                    <AlertTitle fontSize="sm">
                        This requested time is already booked
                    </AlertTitle>
                </HStack>
                <AlertDescription fontSize="sm" w="full">
                    <Text mb={2}>
                        {`Approving would double-book ${clinicianName}. The requested slot overlaps the following ${
                            conflicts.length === 1
                                ? 'appointment'
                                : `${conflicts.length} appointments`
                        }:`}
                    </Text>
                    <Box display="flex" flexDir="column" gap={1.5}>
                        {conflicts.map(appt => (
                            <HStack
                                key={appt.id}
                                align="flex-start"
                                spacing={2}
                            >
                                <Icon
                                    as={FaCalendarTimes}
                                    mt={0.5}
                                    color="red.500"
                                />
                                <Box>
                                    <Text fontWeight="medium">
                                        {`${format(
                                            appt.start_time,
                                            'h:mm a',
                                        )} – ${format(
                                            addMinutes(
                                                appt.start_time,
                                                appt.minutes,
                                            ),
                                            'h:mm a',
                                        )}`}
                                    </Text>
                                    <Text color="gray.600">
                                        {[
                                            getPatientNameFromAppointment(appt),
                                            getServiceNameFromAppointment(appt),
                                        ]
                                            .filter(Boolean)
                                            .join(' · ') || 'Existing appointment'}
                                    </Text>
                                </Box>
                            </HStack>
                        ))}
                    </Box>
                    <Text mt={2}>
                        Ask the client to choose a different time, or deny the
                        request.
                    </Text>
                </AlertDescription>
            </Alert>
        </Box>
    )
}

export default RequestedTimeConflicts
